<script setup>
import {onBeforeMount, ref} from "vue";
import News from '@/components/Dashboard/News/Index';
import CardHeader from '@/components/Dashboard/Card/Header';
import ActivityLog from '@/components/Dashboard/ActivityLog';
import Integration from '@/components/Dashboard/Integration';
import WizardButton from '@/components/Dashboard/Wizard/WizardButton';
import ApprovalNotification from '@/components/Dashboard/Partner/ApprovalNotification.vue';
import PartnerContractNotification from "@/components/Dashboard/Partner/PartnerContractNotification.vue";

const props = defineProps({
  user: Object,
  videoLink: String,
})

const integrations = ref(null)
const loading = ref(true)
const latestActivityIntegration = ref(null)

onBeforeMount(() => Promise
  .all([fetchFirstIntegration(), fetchLatestActivityIntegration()])
  .finally(() => loading.value = false)
)

const fetchFirstIntegration = async () => axios
  .get('/api/integrations', {params: {limit: 1}})
  .then(response => (integrations.value = response.data.data))

const fetchLatestActivityIntegration = async () => axios
  .get(`/dashboard/latest-activity-log`)
  .then(response => (latestActivityIntegration.value = response.data.data.latest_integration_log))

</script>
<template>
  <loading-view :loading="loading">
    <approval-notification v-if="user.is_partner && ! user.partner?.hasSignedContract"/>
    <partner-contract-notification v-if="user.is_partner && user.partner?.hasDraftContract"/>
    <div class="mx-6 mt-4">
      <div class="grid-21">
        <div class="bg_theme p-4 grid-2">
          <section class="flex items-center">
            <div>
              <h3 v-text="trans('dashboard.home.congratulations_x', {name: user.name})"/>
              <p class="py-2" v-text="trans('dashboard.home.intro')"/>
              <inertia-link v-if="$can('integrations.create')" class="button button_secondary"
                            :href="route('wizard.index')"
                            v-text="trans('dashboard.home.start_integrating')"/>
            </div>
          </section>
          <div class="mr-md-3">
            <div class="video_wrap">
              <div style="padding: 56.25% 0 0 0; position: relative">
                <iframe
                  :src="videoLink"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowfullscreen
                  style="position: absolute; top: 0; left: 0; width: 100%; height: 100%"
                  title="APIcenter quick tour"
                />
              </div>
            </div>
          </div>
        </div>
        <News/>
      </div>
      <div class="my-8 grid-3">
        <wizard-button
          v-if="$can('integrations.create')"
          :dynamic="false"
          modifier="border-2 border-dashed flex items-center justify-around cursor-pointer card_add"
          :title="trans('dashboard.home.add_integration')"
        />
        <div class="bg-white">
          <card-header :title="trans('dashboard.home.recent_integration')" v-if="!integrations.length"/>
          <template v-if="integrations.length && $can('integrations.view')">
            <integration :integration="integrations[0]" :user="user"/>
          </template>
          <div v-else class="pb-4 px-4">
            <p class="pt-2">{{ trans('dashboard.home.no_integrations') }}</p>

            <div v-if="$can('integrations.create')">
              <wizard-button :title="trans('dashboard.home.first_integration')"/>
            </div>
          </div>
        </div>
      </div>
      <div v-if="$can('integrations.activity-logs.view|flows.activity-logs.view')" class="p-0 tile my-8">
        <div class="bg-white pt-2">
          <card-header :title="trans('dashboard.home.latest_activity')"/>
        </div>
        <div class="tile shadow-none my-4">
          <activity-log
            v-if="latestActivityIntegration"
            :additional-filters="{}"
            :enable-websocket="false"
            :integration-id="latestActivityIntegration.id"
            :user-id="user.id"
          />
          <div v-else class="pb-4 px-4">
            <p class="pt-2">{{ trans('dashboard.home.no_activities') }}</p>
          </div>
        </div>
      </div>
    </div>
  </loading-view>
</template>

<script>
import DashboardLayout from '@/Layouts/DashboardLayout';

export default {
  layout: DashboardLayout,
};
</script>
